/* eslint-disable no-console */
import axios from 'axios';

import {CLIMATE_DB_BASE_URL, CLIMATE_DB_ENDPOINT_JSON} from '../../../config';
import CRUDMethods from '../../../constants/CRUDConstants';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import usePlatformStore from '../../../store/platformStore/platformStore';
import {checkCurrentYearOrBelow} from '../../../utils/commonFunctions';

import {ensureAIToken} from './aiTokenAPI';

/** One saved universe. A different company id or year is a miss, so the API runs again. */
const PEER_UNIVERSE_STORAGE_KEY = 'peerUniverseByCompanyId';

interface StoredPeerUniverse {
  companyId: string;
  fiscalYear?: string;
  universe: PeerUniverse;
}

/**
 * API 1 — peer universe. Every company from every peer group the company
 * belongs to, listed once and without the company itself, plus the default
 * selection. Carries no metric values, so it is cheap enough to load on entry
 * and reuse for both the Peer Benchmarking and Gap Analysis pages.
 *
 * `GET /api/entities/{entity_id}/peer-universe?fiscal_year=`
 */

export interface PeerGroupOption {
  peer_group_id: string;
  name: string;
  status: string;
  peer_count: number;
  is_default: boolean;
}

export interface PeerUniverseCompany {
  entity_id: number;
  legal_name: string;
  peer_group_ids: string[];
  mongo_company_id: string | null;
  metrics_disclosed: number;
  /** false = too little reported to compare. Selectable, but shown greyed out. */
  has_data: boolean;
  is_default_selected: boolean;
}

/** The companies every average is taken over — combined across all groups. */
export interface PeerIndustry {
  industry_id: string;
  name: string;
  peer_group_ids: string[];
  peer_count: number;
}

export interface PeerUniverse {
  entityId: number | null;
  legalName: string | null;
  year: string | null;
  /** The most peer_ids API 2 accepts in one call. */
  maxSelected: number;
  minDisclosed: number;
  peerGroups: PeerGroupOption[];
  industry: PeerIndustry | null;
  peers: PeerUniverseCompany[];
  defaultPeerIds: number[];
}

interface PeerUniverseResponse {
  entity_id?: number;
  legal_name?: string;
  fiscal_year?: number;
  year?: string;
  default_count?: number;
  max_selected?: number;
  min_disclosed?: number;
  peer_groups?: PeerGroupOption[];
  industry?: PeerIndustry;
  peers?: PeerUniverseCompany[];
  default_peer_ids?: number[];
}

/** Documented ceiling, used until the response states its own `max_selected`. */
const FALLBACK_MAX_SELECTED = 20;

const emptyToNull = (value: string | undefined): string | null => {
  const trimmed = value?.trim() ?? '';

  return trimmed === '' ? null : trimmed;
};

/**
 * APIs 1 and 2 take the year in either form, so the selected year passes
 * straight through. Gap analysis is stricter — see `toYearParam` there.
 */
export const toFiscalYearParam = (selectedYear: string): string | null => {
  const year = selectedYear?.trim() ?? '';

  return /^\d{4}-\d{4}$/.test(year) || /^\d{4}$/.test(year) ? year : null;
};

export const readStoredPeerUniverse = (
  companyId: string,
  selectedYear?: string
): PeerUniverse | null => {
  const id = companyId.trim();

  if (!id) {
    return null;
  }

  try {
    const raw = localStorage.getItem(PEER_UNIVERSE_STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as StoredPeerUniverse;

    if (parsed?.companyId !== id || !parsed.universe) {
      return null;
    }

    // Peer groups differ by year, so a universe saved for another year is a miss.
    if (
      selectedYear !== undefined &&
      parsed.fiscalYear !== toFiscalYearParam(selectedYear)
    ) {
      return null;
    }

    return parsed.universe;
  } catch {
    return null;
  }
};

const writeStoredPeerUniverse = (
  companyId: string,
  fiscalYear: string,
  universe: PeerUniverse
): void => {
  const payload: StoredPeerUniverse = {companyId, fiscalYear, universe};

  localStorage.setItem(PEER_UNIVERSE_STORAGE_KEY, JSON.stringify(payload));
};

const currentFiscalYear = (): string | null => {
  return toFiscalYearParam(
    checkCurrentYearOrBelow(
      usePlatformStore.getState().facilitySelectedYear || null
    )
  );
};

/**
 * Shares one request per company and year so get-token's refresh and a page
 * read do not both hit the network.
 */
let inFlightCompanyId = '';
let inFlightYear = '';
let inFlight: Promise<PeerUniverse | null> | null = null;

const fetchAndStorePeerUniverse = async (
  companyId: string,
  selectedYear: string
): Promise<PeerUniverse | null> => {
  if (
    inFlight &&
    inFlightCompanyId === companyId &&
    inFlightYear === selectedYear
  ) {
    return await inFlight;
  }

  inFlightCompanyId = companyId;
  inFlightYear = selectedYear;
  inFlight = getPeerUniverse(companyId, selectedYear)
    .then((universe) => {
      if (universe) {
        writeStoredPeerUniverse(companyId, selectedYear, universe);
      }

      return universe;
    })
    .finally(() => {
      if (inFlightCompanyId === companyId && inFlightYear === selectedYear) {
        inFlight = null;
        inFlightCompanyId = '';
        inFlightYear = '';
      }
    });

  return await inFlight;
};

/**
 * Uses the universe saved for this company and year. `force` always calls the
 * API, which is what a successful /get-token does. Any other company id or
 * year is a cache miss.
 */
export const syncPeerUniverseForCurrentCompany = async (
  force = false,
  selectedYear?: string
): Promise<PeerUniverse | null> => {
  const companyId = useCompanyStore.getState().companyId?.trim() ?? '';
  const year =
    (selectedYear ? toFiscalYearParam(selectedYear) : null) ??
    currentFiscalYear();

  if (!companyId || !year) {
    return null;
  }

  if (inFlight && inFlightCompanyId === companyId && inFlightYear === year) {
    return await inFlight;
  }

  if (!force) {
    const cached = readStoredPeerUniverse(companyId, year);

    if (cached) {
      return cached;
    }
  }

  return await fetchAndStorePeerUniverse(companyId, year);
};

export const getPeerUniverse = async (
  companyId: string,
  selectedYear: string,
  defaultCount?: number
): Promise<PeerUniverse | null> => {
  const entityId = companyId?.trim() ?? '';
  const fiscalYear = toFiscalYearParam(selectedYear);
  const token = await ensureAIToken();

  if (!entityId || !fiscalYear || !token) {
    return null;
  }

  const params: Record<string, string | number> = {fiscal_year: fiscalYear};

  // Omitted entirely so the platform applies the tenant's configured default.
  if (defaultCount !== undefined) {
    params.default_count = defaultCount;
  }

  try {
    const response = await axios.request<PeerUniverseResponse>({
      method: CRUDMethods.GET,
      url: [
        CLIMATE_DB_BASE_URL,
        CLIMATE_DB_ENDPOINT_JSON.entities,
        '/',
        encodeURIComponent(entityId),
        CLIMATE_DB_ENDPOINT_JSON.peerUniverse
      ].join(''),
      params,
      headers: {Authorization: `Bearer ${token}`}
    });

    const data = response.data ?? {};

    return {
      entityId: data.entity_id ?? null,
      legalName: emptyToNull(data.legal_name),
      year: emptyToNull(data.year),
      maxSelected: data.max_selected ?? FALLBACK_MAX_SELECTED,
      minDisclosed: data.min_disclosed ?? 0,
      peerGroups: data.peer_groups ?? [],
      industry: data.industry ?? null,
      // Already in display order: defaults first, then by metrics_disclosed.
      peers: data.peers ?? [],
      defaultPeerIds: data.default_peer_ids ?? []
    };
  } catch (err) {
    console.log('peer universe err:', err);

    return null;
  }
};
